/**
 * Logique SuperAdmin (US-506/511/512) — console centrale d'Ahmed.
 * - Catalogue d'options/modules activables par client (master library).
 * - Activation/désactivation par tenant (feature flags).
 * - Statut « santé » du parc (porté du legacy : actif / en veille / à relancer).
 * Pur et testable. La liste unifiée des tenants vient de lib/tenants.ts (loadAllTenants).
 */

/** Modules/options proposables aux clients (catalogue central — US-511). */
export const FEATURE_CATALOG = {
  loyalty: 'Fidélité (cagnotte)',
  delivery: 'Livraison',
  kiosk: 'Mode kiosque QR',
  online_payment: 'Paiement en ligne',
  reviews: 'Avis Google & Satisfaction (NPS)',
  campaigns: 'Campagnes marketing',
  yumlink_controle: 'YumLink Contrôle',
} as const;

export type FeatureKey = keyof typeof FEATURE_CATALOG;

/** Flags d'un tenant : quelles fonctionnalités sont activées. */
export type TenantFeatures = Partial<Record<FeatureKey, boolean>>;

export function isFeatureEnabled(features: TenantFeatures, key: FeatureKey): boolean {
  return features[key] === true;
}

/**
 * Active/désactive une option pour un client (US-512) — retourne un NOUVEL objet (immutable).
 * Réversible : l'historique d'activation est géré côté persistance.
 */
export function setFeature(
  features: TenantFeatures,
  key: FeatureKey,
  enabled: boolean,
): TenantFeatures {
  return { ...features, [key]: enabled };
}

/** Liste lisible des options actives d'un client. */
export function activeFeatureLabels(features: TenantFeatures): string[] {
  return (Object.keys(FEATURE_CATALOG) as FeatureKey[])
    .filter((k) => isFeatureEnabled(features, k))
    .map((k) => FEATURE_CATALOG[k]);
}

export type ParcHealth = 'actif' | 'en veille' | 'à relancer';

/**
 * Statut santé d'un resto selon ses commandes sur 30 jours (seuils portés du legacy) :
 *   >= 15 → actif · 5-14 → en veille · < 5 → à relancer.
 */
export function parcHealthStatus(orders30d: number): ParcHealth {
  if (orders30d >= 15) return 'actif';
  if (orders30d >= 5) return 'en veille';
  return 'à relancer';
}
